import { A } from '@solidjs/router';
import { createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { DeployStackDialog } from '../components/DeployStackDialog';
import { EmptyState, ErrorAlert, LoadingRows } from '../components/states';
import { useAuth } from '../lib/AuthContext';
import { flatten, useStacks } from '../lib/queries';
import type { StackWithManifest } from '../lib/types';
import { Badge } from '../ui/Badge';
import { Button, buttonClass } from '../ui/Button';
import { Card } from '../ui/Card';
import { Icon } from '../ui/Icon';

export function Stacks(): JSX.Element {
  const auth = useAuth();
  const stacks = useStacks();
  // The catalog does not ask for non-manifest prefixes, so this only drops
  // anything the backend starts reporting without one.
  const items = createMemo(() =>
    flatten(stacks.data?.pages).filter((stack): stack is StackWithManifest => stack.manifest === '1'),
  );
  const [target, setTarget] = createSignal<StackWithManifest>();

  return (
    <main class="page">
      <div class="page__head">
        <div class="page__title">
          <h1>Stacks</h1>
          <span class="page__subtitle">Docker Compose stacks that can be deployed onto a ready box.</span>
        </div>
        <div class="page__actions">
          <Button variant="secondary" onClick={() => void stacks.refetch()} loading={stacks.isFetching}>
            <Icon name="refresh" />
            Refresh
          </Button>
          <Show when={auth.config.adminEnabled}>
            <A href="/admin/stacks" class={buttonClass('ghost', 'md')}>
              <Icon name="shield" />
              Register stack
            </A>
          </Show>
        </div>
      </div>

      <Show when={stacks.isError}>
        <ErrorAlert title="Could not load the catalog" error={stacks.error} onRetry={() => void stacks.refetch()} />
      </Show>

      <Show when={!stacks.isPending} fallback={<LoadingRows rows={3} height={96} />}>
        <Show
          when={items().length > 0}
          fallback={
            <EmptyState
              icon="layers"
              title="The catalog is empty"
              text="Stacks are registered by an administrator before they can be deployed."
              action={
                auth.config.adminEnabled ? (
                  <A href="/admin/stacks" class={buttonClass('secondary', 'md')}>
                    Register a stack
                  </A>
                ) : undefined
              }
            />
          }
        >
          <div class="list">
            <For each={items()}>
              {(stack) => (
                <Card padding="md">
                  <div class="row gap-4 wrap">
                    <Icon name="layers" size={18} />
                    <div class="list-row__main">
                      <span class="list-row__title">{stack.display}</span>
                      <span class="list-row__meta mono">{stack.slug}</span>
                    </div>
                    <Badge tone="neutral">{stack.type === 'DockerCompose' ? 'Docker Compose' : stack.type}</Badge>
                    <Badge tone="brand">rev {stack.revision}</Badge>
                    <Button variant="secondary" size="sm" onClick={() => setTarget(stack)}>
                      Deploy
                    </Button>
                  </div>
                </Card>
              )}
            </For>
          </div>
        </Show>
      </Show>

      <Show when={stacks.hasNextPage}>
        <div class="row gap-3" style={{ 'justify-content': 'center', 'margin-top': 'var(--space-6)' }}>
          <Button variant="secondary" loading={stacks.isFetchingNextPage} onClick={() => void stacks.fetchNextPage()}>
            Load more
          </Button>
        </div>
      </Show>

      <DeployStackDialog open={target() !== undefined} stack={target()} onClose={() => setTarget(undefined)} />
    </main>
  );
}
